import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { API_BASE_URL } from '../../../../config/api';
import {
  FaSignOutAlt,
  FaArrowLeft,
  FaFilter,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaExclamationTriangle,
} from 'react-icons/fa';

import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import EventImage from '../../../../components/EventImage.jsx';
import NoEventsMessage from '../../../../components/common/NoEventsMessage.jsx';
import '../css/RehostEventForm.css';

const RehostEventForm = () => {
  const navigate = useNavigate();
  const [pastEvents, setPastEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatDate = (dateString) => {
    if (!dateString) {
      console.warn('Date string is empty or null');
      return 'Date not specified';
    }

    try {
      const [year, month, day] = dateString.split('-');
      if (!year || !month || !day || year.length !== 4 || isNaN(Date.parse(`${year}-${month}-${day}`))) {
        throw new Error('Invalid date format');
      }

      const date = new Date(`${year}-${month}-${day}`);
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date');
      }

      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Date formatting error:', error, 'Date string:', dateString);
      return 'Date not specified';
    }
  };

  const getBackgroundStyle = (background) => {
    if (!Array.isArray(background) || background.length === 0) {
      return {};
    }

    const mode = background[0];
    const value1 = background[1];
    const value2 = background[2];

    if (mode === 'solid' && value1) {
      return {
        backgroundColor: value1,
        backgroundImage: 'none',
      };
    }

    if (mode === 'gradient' && value1 && value2) {
      return {
        backgroundImage: `linear-gradient(90deg, ${value1}, ${value2})`,
        backgroundColor: 'transparent',
      };
    }

    if (mode === 'image' && value1) {
      return {
        backgroundImage: `url(${value1})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'transparent',
      };
    }

    return {};
  };

  const extractLowestPrice = (packages) => {
    if (!packages || !Array.isArray(packages) || packages.length === 0) {
      console.warn('No valid packages found');
      return 'N/A';
    }

    try {
      const prices = packages
        .map((pkg) => {
          try {
            const parsedPkg = typeof pkg === 'string' ? JSON.parse(pkg) : pkg;
            return parsedPkg.pricing ? parseFloat(parsedPkg.pricing.replace(/[^0-9.]/g, '')) : null;
          } catch (e) {
            console.warn(`Failed to parse package: ${JSON.stringify(pkg)}`, e);
            return null;
          }
        })
        .filter((price) => price !== null && !isNaN(price));

      if (prices.length === 0) {
        console.warn('No valid prices found in packages');
        return 'N/A';
      }

      const lowestPrice = Math.min(...prices);
      return `R ${lowestPrice.toFixed(2)}`;
    } catch (error) {
      console.error('Price extraction error:', error);
      return 'N/A';
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPastEvents = async () => {
      if (!isMounted) return;

      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        let userId = localStorage.getItem('userId');

        if (!userId) {
          const userData = localStorage.getItem('user');
          if (userData) {
            try {
              const user = JSON.parse(userData);
              userId = user.id;
              if (userId) {
                localStorage.setItem('userId', userId);
              }
            } catch (e) {
              console.error('Error parsing user data:', e);
            }
          }
        }

        if (!token || !userId) {
          console.warn('Authentication required - No token or user ID found');
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('user');
          navigate('/');
          return;
        }

        try {
          const tokenParts = token.split('.');
          if (tokenParts.length !== 3) {
            throw new Error('Invalid token format');
          }
          const tokenPayload = JSON.parse(atob(tokenParts[1]));
          const now = Math.floor(Date.now() / 1000);
          if (tokenPayload.exp && tokenPayload.exp < now) {
            throw new Error('Token expired');
          }
        } catch (tokenError) {
          console.error('Token validation error:', tokenError);
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('user');
          navigate('/');
          return;
        }

        const apiUrl = `${API_BASE_URL}/organiser/events`;
        console.log('[RehostEventForm] 📋 Fetching all events — API: GET /organiser/events');

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        console.log('[RehostEventForm] Response status:', response.status, response.statusText);

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (e) {
            console.warn('Failed to parse error response:', e);
          }
          console.error('Server responded with error:', {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            error: errorData,
          });
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('user');
            navigate('/');
            return;
          }
          throw new Error(errorData.error || `Server error: ${response.status} ${response.statusText}`);
        }

        let data = [];
        try {
          data = await response.json();
          console.log('[RehostEventForm] ✅ All events loaded:', {
            columns: 'id/event_id, event_name/name, location, start_date/event_date/startdate, end_date/enddate, start_time/event_time/time, cover_image_url/coverimage, background_images_array/background, packages[{name,price,...}], tab_sections[{name,content}]',
            total: data?.length,
          });
        } catch (e) {
          console.error('Failed to parse response JSON:', e);
          data = [];
        }

        const eventsData = Array.isArray(data) ? data : [];

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isFinishedEvent = (event) => {
          const rawEnd = event.end_date || event.enddate || event.EndDate || null;
          if (!rawEnd) return false;
          const end = new Date(rawEnd);
          if (Number.isNaN(end.getTime())) return false;
          end.setHours(0, 0, 0, 0);
          // Finished means EndDate strictly before today
          return end < today;
        };

        // Only keep finished events for rehosting
        const finishedEvents = eventsData.filter(isFinishedEvent);

        // Normalise field names and shapes for the UI.
        console.log('[RehostEventForm] 🔍 Filtering for finished events:', { total: eventsData.length, finished: finishedEvents.length });
        const eventsWithFormattedData = finishedEvents.map((event) => {
          return {
            id: event.id || event.event_id || '',
            eventid: event.id || event.event_id || '',
            event_name: event.event_name || event.name || 'Unnamed Event',
            location: event.location || 'Location not specified',
            // Use the new normalised schema fields from the Events table
            // StartDate / StartTime are the source of truth for the original
            // event datetime shown on the rehost cards.
            date: event.start_date || event.event_date || event.startdate || 'Date not specified',
            enddate: event.end_date || event.enddate || '',
            time: event.start_time || event.event_time || event.time || 'Time not specified',
            // Event type for downstream views
            event_type: event.event_type || event.type || '',
            // packages now come from the backend as JSON, already normalized
            price: extractLowestPrice(event.packages),
            status: event.is_approved ? 'approved' : 'pending',
            file_url: event.cover_image_url || event.coverimage || '/default-event-image.jpg',
            cover_image_url: event.cover_image_url || event.coverimage || '/default-event-image.jpg',
            description: event.description || 'No description available',
            terms_and_conditions: event.terms_and_conditions || '',
            background: event.background_images_array || event.background || null,
            background_images_array: event.background_images_array || event.background || null,
            // Keep the full backend packages and tab sections for the details page
            packages: Array.isArray(event.packages) ? event.packages : [],
            tab_sections: Array.isArray(event.tab_sections) ? event.tab_sections : [],
          };
        });

        if (isMounted) {
          setPastEvents(eventsWithFormattedData);
          setFilteredEvents(eventsWithFormattedData);
          setError(null);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        if (isMounted) {
          setError(err.message || 'Failed to load past events. Please try again later.');
          setPastEvents([]);
          setFilteredEvents([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPastEvents();
    const interval = setInterval(fetchPastEvents, 10 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [navigate]);

  const handleImageError = (e) => {
    if (e.target.src.includes('fallback-logo.png') || e.target.classList.contains('image-error')) {
      return;
    }

    console.warn(`Failed to load image: ${e.target.src}`);
    e.target.src = '/fallback-logo.png';
    e.target.classList.add('image-error');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="rehost-loading-container">
          <ClipLoader color="#3a7bd5" loading={loading} size={50} />
          <p className="rehost-loading-text">Loading past events...</p>
        </div>
      );
    }

    if (error) {
      const isAuthError = error.includes('log in') || error.includes('expired');

      return (
        <div className="rehost-error-container">
          <FaExclamationTriangle size={50} color="#e53935" />
          <h3 className="rehost-title">
            {isAuthError ? 'Authentication Required' : 'Error Loading Past Events'}
          </h3>
          <p className="rehost-error-message">{error}</p>
          <div>
            {isAuthError ? (
              <button
                className="rehost-view-button"
                onClick={() => {
                  localStorage.clear();
                  navigate('/');
                }}
              >
                Go to Login
              </button>
            ) : (
              <button
                className="rehost-view-button"
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  setPastEvents([]);
                  setFilteredEvents([]);
                }}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      );
    }

    // Main content when data is loaded successfully
    return (
      <>
        {filteredEvents.length === 0 ? (
          <NoEventsMessage />
        ) : (
          <div className="rehost-card-grid">
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                className="rehost-card"
              >
                <div
                  className="rehost-card-image-container"
                  style={getBackgroundStyle(event.background)}
                >
                  <EventImage
                    image={event.file_url}
                    coverimage={event.file_url}
                    alt={event.event_name}
                    className="rehost-card-image"
                    loading="eager"
                  />
                </div>
                <div className="rehost-card-content">
                  <h3 className="rehost-card-title">{event.event_name}</h3>
                  <div className="rehost-card-details">
                    <p className="rehost-detail-row">
                      <FaMapMarkerAlt className="rehost-detail-icon-location" /> {event.location}
                    </p>
                    <p className="rehost-detail-row">
                      <FaCalendarAlt className="rehost-detail-icon-date" /> {formatDate(event.date)}
                    </p>
                    <p className="rehost-detail-row">
                      <FaClock className="rehost-detail-icon-time" /> {event.time}
                    </p>
                  </div>
                </div>
                <div className="rehost-card-bottom">
                  <span
                    className={`rehost-status ${
                      event.status === 'pending' ? 'rehost-status--pending' : 'rehost-status--approved'
                    }`}
                  >
                    Status: {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                  </span>
                  <button
                    className="rehost-view-button"
                    onClick={() => navigate('/view-past-event', { state: { event } })}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="rehost-container">
      <GlobalHeader
        title="Rehost Event"
        subtitle="Select a past event to rehost"
        backTo="/organiser-dash"
        logoutTo="/"
        onLogout={() => {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('user');
          navigate('/');
        }}
      />
      {renderContent()}
    </div>
  );
};

export default RehostEventForm;
